import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Skeleton } from "@akuna/ui";
import { UserPlus } from "lucide-react";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { useCommissionModels, useMemberModels } from "@/hooks/useCommissions";
import type { CommissionModel, MemberModel } from "@/lib/commissions/types";
import { AssignModelDialog } from "@/components/commissions/AssignModelDialog";
import { cn } from "@/lib/utils";

function People({ people }: { people: MemberModel[] }) {
  return (
    <ul className="flex flex-wrap gap-x-1 text-sm">
      {people.map((p, i) => (
        <li key={p.user_id}>
          <Link
            to={`/team/${p.user_id}/remuneracao`}
            className="rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {p.full_name}
          </Link>
          {i < people.length - 1 && ","}
        </li>
      ))}
    </ul>
  );
}

/** Who is on which commission model, with the shortcut to assign people. */
export function CommissionAssignmentsSection() {
  const { t } = useTranslation("commissions");
  const { hasPermission: canEdit } = useHasPermissionSync("commissions", "edit");
  const { data: models = [], isLoading: loadingModels } = useCommissionModels();
  const { data: members = [], isLoading: loadingMembers } = useMemberModels();
  const [assigning, setAssigning] = useState<CommissionModel | null>(null);

  if (loadingModels || loadingMembers) return <Skeleton label={t("assignments.title")} className="h-40 w-full" />;

  const withoutModel = members.filter((m) => !m.model_id);
  const shown = models.filter((m) => m.is_active || members.some((p) => p.model_id === m.id));

  return (
    <section aria-labelledby="commission-assignments" className="flex flex-col gap-4 rounded-card border border-border bg-card p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 id="commission-assignments" className="text-base font-semibold">{t("assignments.title")}</h2>
          <p className="text-sm text-muted-foreground">{t("assignments.description")}</p>
        </div>
        <Button asChild variant="outline"><Link to="/catalog/commissions">{t("assignments.editModels")}</Link></Button>
      </div>

      {shown.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("assignments.noModels")}</p>
      ) : (
        <ul className="grid grid-cols-[repeat(auto-fit,minmax(15rem,1fr))] gap-3">
          {shown.map((model) => {
            const people = members.filter((m) => m.model_id === model.id);
            return (
              <li key={model.id} className="flex flex-col gap-2.5 rounded-md border border-border p-4">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate font-semibold">{model.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{t("assignments.count", { count: people.length })}</span>
                </div>
                {people.length === 0 ? <p className="text-sm text-muted-foreground">{t("assignments.nobody")}</p> : <People people={people} />}
                {canEdit && (
                  <Button variant="ghost" size="sm" className="mt-auto self-start px-0 text-primary" onClick={() => setAssigning(model)}>
                    <UserPlus className="h-4 w-4" aria-hidden="true" />
                    {t("assign.open")}
                  </Button>
                )}
              </li>
            );
          })}
          {withoutModel.length > 0 && (
            <li className={cn("flex flex-col gap-2.5 rounded-md border border-dashed border-state-warning bg-state-warning-soft p-4 text-state-warning-foreground")}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-semibold">{t("assignments.withoutModel")}</span>
                <span className="shrink-0 text-xs">{t("assignments.count", { count: withoutModel.length })}</span>
              </div>
              <People people={withoutModel} />
              <p className="text-xs">{t("assignments.withoutModelHint")}</p>
            </li>
          )}
        </ul>
      )}

      <AssignModelDialog model={assigning} open={assigning !== null} onOpenChange={(open) => !open && setAssigning(null)} />
    </section>
  );
}
